// Hand-drawn text marks (from componentry.dev "annotated text"): a double underline and a box.
// Each mark is generated, not drawn: a seeded random walk nudges every sampled point off its ideal
// line, so it reads as drawn by hand but looks the same on every load. The stroke draws itself in
// when the mark first scrolls into view. Reduced motion: shown straight away, no drawing.
(function () {
  const { useEffect, useRef } = React;
  const h = React.createElement;

  function createRandom(seed) {
    let state = seed >>> 0;
    return function next() { state = (state * 1664525 + 1013904223) >>> 0; return state / 4294967296; };
  }
  const shake = (rand, amount) => (rand() - 0.5) * 2 * amount;
  const round = (v) => Math.round(v * 100) / 100;

  // Curve through the points: anchor on each, pass through the midpoints.
  function trace(points, close) {
    const [first, ...rest] = points;
    if (!first) return "";
    let d = "M" + round(first.x) + "," + round(first.y);
    rest.forEach((p, i) => {
      const next = rest[i + 1];
      if (!next) { d += " L" + round(p.x) + "," + round(p.y); return; }
      d += " Q" + round(p.x) + "," + round(p.y) + " " + round((p.x + next.x) / 2) + "," + round((p.y + next.y) / 2);
    });
    return close ? d + " Z" : d;
  }

  // A nearly straight stroke that bows by `sag` and trembles by `wobble`; endpoints stay put.
  function stroke(from, to, opts, rand) {
    const sag = opts.sag || 0, wobble = opts.wobble == null ? 0.55 : opts.wobble, steps = opts.steps || 12;
    const pts = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps, loose = i === 0 || i === steps ? 0 : 1;
      pts.push({
        x: from.x + (to.x - from.x) * t + shake(rand, wobble) * loose,
        y: from.y + (to.y - from.y) * t + Math.sin(t * Math.PI) * sag + shake(rand, wobble) * loose,
      });
    }
    return pts;
  }

  const MARKS = {
    doubleUnderline: {
      viewBox: "0 0 140 16",
      strokes: (() => {
        const rand = createRandom(41);
        return [
          { d: trace(stroke({ x: 3, y: 5 }, { x: 137, y: 4 }, { sag: -2 }, rand)), width: 2.4 },
          { d: trace(stroke({ x: 5, y: 12 }, { x: 135, y: 11 }, { sag: -1.2 }, rand)), width: 1.8, opacity: 0.75 },
        ];
      })(),
    },
    box: {
      viewBox: "0 0 200 64",
      strokes: (() => {
        const rand = createRandom(127);
        const tl = { x: 12, y: 10 }, tr = { x: 188, y: 10 }, br = { x: 188, y: 54 }, bl = { x: 12, y: 54 };
        // The long edges bow outward; the short ones just tremble.
        const outline = [
          ...stroke(tl, tr, { sag: -3, steps: 8 }, rand),
          ...stroke(tr, br, { steps: 4 }, rand),
          ...stroke(br, bl, { sag: 3, steps: 8 }, rand),
          ...stroke(bl, tl, { steps: 4 }, rand),
        ];
        return [{ d: trace(outline, true), width: 2.6 }];
      })(),
    },
    // Square version of the box for square things (icon tiles), so the stroke is not stretched.
    boxSquare: {
      viewBox: "0 0 64 64",
      strokes: (() => {
        const rand = createRandom(131);
        const tl = { x: 5, y: 6 }, tr = { x: 59, y: 5 }, br = { x: 58, y: 59 }, bl = { x: 6, y: 58 };
        const outline = [
          ...stroke(tl, tr, { sag: -1.6, steps: 6, wobble: 0.5 }, rand),
          ...stroke(tr, br, { sag: 1.2, steps: 6, wobble: 0.5 }, rand),
          ...stroke(br, bl, { sag: 1.6, steps: 6, wobble: 0.5 }, rand),
          ...stroke(bl, tl, { sag: -1.2, steps: 6, wobble: 0.5 }, rand),
        ];
        return [{ d: trace(outline, true), width: 2.2 }];
      })(),
    },
  };

  function PtAnnotate({ variant = "doubleUnderline", className, delay = 0, duration = 0.65, children }) {
    const ref = useRef(null);
    const mark = MARKS[variant] || MARKS.doubleUnderline;

    useEffect(() => {
      const el = ref.current;
      const calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (!el || calm || !el.animate || !window.IntersectionObserver) return;
      const anims = [...el.querySelectorAll("[data-an-stroke]")].map((path, i) => {
        const op = Number(path.getAttribute("opacity") || 1);
        const a = path.animate([
          // a zero-length dash still paints a round cap: keep it invisible until the stroke moves
          { strokeDasharray: "1", strokeDashoffset: "1", opacity: 0, offset: 0 },
          { strokeDasharray: "1", strokeDashoffset: "0.999", opacity: op, offset: 0.001 },
          { strokeDasharray: "1", strokeDashoffset: "0", opacity: op, offset: 1 },
        ], { duration: duration * 1000, delay: delay * 1000 + i * 160, easing: "cubic-bezier(0.22, 0.61, 0.36, 1)", fill: "backwards" });
        a.pause();
        return a;
      });
      const io = new IntersectionObserver(([e]) => {
        if (!e || !e.isIntersecting) return;
        anims.forEach((a) => a.play());
        io.disconnect();
      }, { threshold: 0.25 });
      io.observe(el);
      return () => { io.disconnect(); anims.forEach((a) => a.cancel()); };
    }, [variant, delay, duration]);

    return h("span", { ref, className: "an an-" + variant + (className ? " " + className : "") },
      children,
      h("svg", { className: "an-deco", viewBox: mark.viewBox, fill: "none", preserveAspectRatio: "none", "aria-hidden": "true", focusable: "false" },
        mark.strokes.map((s) => h("path", {
          key: s.d, d: s.d, pathLength: 1, "data-an-stroke": "", fill: "none", stroke: "currentColor",
          strokeWidth: s.width || 2.4, strokeLinecap: "round", strokeLinejoin: "round", opacity: s.opacity,
        }))));
  }

  window.PtAnnotate = PtAnnotate;
})();
